import { useState, useEffect, useCallback } from "react";
import {
  getEventTypes,
  getAvailableSlots,
  createBooking,
  getUserById,
} from "../../helpers/calcom_helper";
import { toast } from "react-toastify";

import LeftSideContent from "./LeftSideContent";
import MeetingDetails from "./MeetingDetails";
import CalendarStep from "./CalendarStep";
import TimeSelectionStep from "./TimeSelectionStep";
import ConfirmationStep from "./ConfirmationStep";
import {
  MeetingDetailsSkeleton,
  CalendarSkeleton,
  TimeSelectionSkeleton,
  ConfirmationSkeleton,
} from "./BookingSkeletons";
import { MEET_IDS } from "../../constants/cal";

const BookingSection = ({ userFormData, onBookingComplete }) => {
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const loadEventTypes = useCallback(async () => {
    try {
      setLoading(true);

      const types = await getEventTypes();

      if (types.length > 0) {
        const selected = types.find(
          (type) => type.id === Number(MEET_IDS[userFormData.subject])
        );
        if (selected) {
          setSelectedEventType(selected);
        } else {
          setSelectedEventType(types[0]);
        }

        if (types[0].owner?.id) {
          try {
            const userInfo = await getUserById(types[0].owner.id);
            setUserDetails(userInfo);
          } catch (userError) {
            console.log("Error fetching user details:", userError);
          }
        }
      } else {
        console.log("No event types found in your Cal.com account");
      }
    } catch (error) {
      console.log(`Failed to load event types: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [userFormData.subject]);

  useEffect(() => {
    loadEventTypes();
  }, [loadEventTypes]);

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(2);
    setLoading(true);

    try {
      const [year, month, day] = date.split("-").map(Number);
      const selectedDate = new Date(year, month - 1, day);
      selectedDate.setHours(0, 0, 0, 0);

      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      const slots = await getAvailableSlots(
        selectedEventType.id,
        selectedDate.toISOString(),
        endDate.toISOString()
      );

      const dateStr = date;
      const availableSlotsForDate = slots.slots?.[dateStr] || [];
      setAvailableSlots(availableSlotsForDate);

      if (availableSlotsForDate.length === 0) {
        console.log(
          `No available time slots found for ${selectedDate.toLocaleDateString()}. Please try a different date.`
        );
      }
    } catch (error) {
      console.log(`Failed to load available time slots: ${error.message}`);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBooking = async () => {
    if (!selectedSlot || !userFormData) return;

    try {
      setLoading(true);
      const bookingData = {
        eventTypeId: selectedEventType.id,
        start: selectedSlot.time,
        responses: {
          name: `${userFormData.fullName} (${userFormData.company})`,
          email: userFormData.email,
          location: "integrations:google_meet", // Changed from object to string
        },
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: "en",
        metadata: {
          company: userFormData.company,
          industry: userFormData.industry,
          companySize: userFormData.companySize,
          country: userFormData.country,
          subject: userFormData.subject,
          message: userFormData.body,
        },
      };

      await createBooking(bookingData);
      toast.success("Demo booked successfully!");
      onBookingComplete();
    } catch (error) {
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (
      let d = new Date(startDate);
      d <= lastDay;
      d.setDate(d.getDate() + 1)
    ) {
      days.push(new Date(d));
    }
    return days;
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    if (dateOnly < today) return true;

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return true;

    return false;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatTime = (timeString) => {
    const date = new Date(timeString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="font-sans flex justify-center py-4 md:px-0 px-4 lg:py-10">
      <div className="max-w-6xl w-full">
        <div className="grid lg:grid-cols-[30%_70%] gap-8 lg:gap-12">
          <LeftSideContent loading={loading} step={step} />

          <div className="bg-white border border-gray-200 rounded-lg flex flex-wrap">
            {selectedEventType && (
              <MeetingDetails
                selectedEventType={selectedEventType}
                userDetails={userDetails}
              />
            )}

            {loading && step === 1 && (
              <div className="p-5 flex-1">
                <MeetingDetailsSkeleton />
                <CalendarSkeleton />
              </div>
            )}

            {loading && step === 2 && <TimeSelectionSkeleton />}

            {loading && step === 3 && <ConfirmationSkeleton />}

            {step === 1 && !loading && (
              <CalendarStep
                currentMonth={currentMonth}
                navigateMonth={navigateMonth}
                getCalendarDays={getCalendarDays}
                isDateDisabled={isDateDisabled}
                isToday={isToday}
                handleDateSelect={handleDateSelect}
              />
            )}

            {step === 2 && !loading && (
              <TimeSelectionStep
                selectedDate={selectedDate}
                availableSlots={availableSlots}
                handleSlotSelect={handleSlotSelect}
                setStep={setStep}
                formatTime={formatTime}
              />
            )}

            {step === 3 && !loading && (
              <ConfirmationStep
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                selectedEventType={selectedEventType}
                formatTime={formatTime}
                setStep={setStep}
                handleBooking={handleBooking}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSection;
